// Lesson post fill rules, shared by the Mac render script (05-SOCIAL/scripts/render-lesson-posts.mjs),
// the engine worker (Social panel import and approve) and the engine web (Social panel live preview).
// Recipe: "How the engine renders a post" in 05-SOCIAL/Social-Design-Mockups/templates/SOCIAL-TEMPLATE-SLOTS.md.
//
// Pure functions only: no file system, no network, no DOM. Every rule a post type needs comes from its
// package (05-SOCIAL/scripts/post-types.json plus slots.json), so a new type is data, not code.
//
// A package looks like:
//   { type, label, template, slides, slots: [...], accent_slots: [...], optional_slots: [...],
//     pose_slots: [...], pose_sets: { 'cover.pose': ['P04', ...] } (only when the type has one), fixed_slots: [...],
//     themes: [...], platforms: [{ name, file, width, height, limit, sha256 }] }

export class FillStop extends Error {} // a problem that stops the post before anything is rendered

// A pose code is P plus two digits, as LIBRARY.md lists them. Any code with an approved image may be used (Odin 2026-10-29;
// was P01 to P20): the Mac checks LIBRARY.md and 02-approved, the Engine checks the poses synced from the Mac.
export const POSE_CODE_RE = /^P\d{2}$/;

// A pose slot with a set in its type's package may be given as "auto" (or left out): the Engine picks it on import
// with pickPose (Odin 2026-10-31, "Lesson post pose rotation").
export const POSE_AUTO = 'auto';

export function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// LIBRARY.md lines "- P04 -> ml-unimpressed-full.png ..." -> { P04: 'ml-unimpressed-full.png' }
export function parsePoseLibrary(text) {
  const map = {};
  for (const m of String(text).matchAll(/^- (P\d{2}) -> (ml-[a-z0-9-]+\.png)/gm)) map[m[1]] = m[2];
  return map;
}

function stringList(v, what) {
  if (!Array.isArray(v) || v.some(x => typeof x !== 'string' || !x)) throw new FillStop(`package ${what} must be a list of names`);
  return [...v];
}

// pose_sets in pose_slots order. Each set: two or more different pose codes (one code could never rotate).
function poseSets(v, poseSlots) {
  if (v === undefined || v === null) return {};
  if (typeof v !== 'object' || Array.isArray(v)) throw new FillStop('package pose_sets must map a pose slot to a list of pose codes');
  const unknown = Object.keys(v).filter(k => !poseSlots.includes(k));
  if (unknown.length) throw new FillStop(`package pose_sets names slots that are not pose slots: ${unknown.join(', ')}`);
  const out = {};
  for (const slot of poseSlots) {
    if (!(slot in v)) continue;
    const set = v[slot];
    if (!Array.isArray(set) || set.length < 2 || set.some(c => !POSE_CODE_RE.test(c ?? '')) || new Set(set).size !== set.length) {
      throw new FillStop(`package pose_sets ${slot} must be two or more different pose codes like P04`);
    }
    out[slot] = [...set];
  }
  return out;
}

/**
 * A package in its one fixed shape and key order, so the Mac and the engine hash the same bytes
 * (sha256 of JSON.stringify(normalizePackage(pkg))). Throws FillStop on a malformed package.
 */
export function normalizePackage(pkg) {
  if (!pkg || typeof pkg !== 'object') throw new FillStop('package is missing');
  for (const k of ['type', 'label', 'template']) {
    if (typeof pkg[k] !== 'string' || !pkg[k].trim()) throw new FillStop(`package ${k} is empty`);
  }
  if (!/^[a-z0-9-]+$/.test(pkg.type)) throw new FillStop(`package type "${pkg.type}" must be lowercase letters, digits and hyphens`);
  if (!Number.isInteger(pkg.slides) || pkg.slides < 1) throw new FillStop('package slides must be a whole number');
  if (!Array.isArray(pkg.platforms) || !pkg.platforms.length) throw new FillStop('package has no platforms');
  const poseSlots = stringList(pkg.pose_slots, 'pose_slots');
  const sets = poseSets(pkg.pose_sets, poseSlots);
  return {
    type: pkg.type, label: pkg.label, template: pkg.template, slides: pkg.slides,
    slots: stringList(pkg.slots, 'slots'),
    accent_slots: stringList(pkg.accent_slots, 'accent_slots'),
    optional_slots: stringList(pkg.optional_slots, 'optional_slots'),
    pose_slots: poseSlots,
    // Left out when empty, so a type with no set keeps the package (and package_sha) it had before pose sets.
    ...(Object.keys(sets).length ? { pose_sets: sets } : {}),
    fixed_slots: stringList(pkg.fixed_slots, 'fixed_slots'),
    themes: stringList(pkg.themes, 'themes'),
    platforms: pkg.platforms.map(p => {
      if (!p || typeof p.name !== 'string' || typeof p.file !== 'string' || !/^[0-9a-f]{64}$/.test(p.sha256 ?? '')) {
        throw new FillStop('each package platform needs name, file and sha256');
      }
      for (const k of ['width', 'height', 'limit']) {
        if (!Number.isInteger(p[k]) || p[k] < 1) throw new FillStop(`platform ${p.name} ${k} must be a whole number`);
      }
      return { name: p.name, file: p.file, width: p.width, height: p.height, limit: p.limit, sha256: p.sha256 };
    }),
  };
}

/** The pose slots of this post left to the Engine: "auto" or left out, on a slot whose type has a set. */
export function autoPoseSlots(post, pkg) {
  const sets = pkg.pose_sets || {};
  return pkg.pose_slots.filter(s => sets[s] && [undefined, null, '', POSE_AUTO].includes(post[s]));
}

/**
 * The rotation rule (Odin 2026-10-31): never the code of the type's most recent post for that slot; among the rest,
 * the one used longest ago, a code never used counting as oldest (ties go to set order).
 * history: that slot's codes on the type's earlier posts, most recent first (fixed codes count too).
 */
export function pickPose(set, history) {
  const last = history[0];
  const lastUse = code => { const i = history.indexOf(code); return i < 0 ? Infinity : i; };
  const choices = set.filter(c => c !== last);
  return choices.reduce((best, c) => (lastUse(c) > lastUse(best) ? c : best), choices[0]);
}

/** The checks render-lesson-posts.mjs makes on a post file, against its type's package. Throws FillStop. */
export function validatePost(post, pkg) {
  if (post.theme && !pkg.themes.includes(post.theme)) throw new FillStop(`theme must be blank or one of ${pkg.themes.join(', ')} (got "${post.theme}")`);
  if (typeof post.caption !== 'string' || !post.caption.trim()) throw new FillStop('caption is empty');
  const auto = new Set(autoPoseSlots(post, pkg));
  for (const s of pkg.pose_slots) {
    if (auto.has(s) || POSE_CODE_RE.test(post[s] ?? '')) continue;
    if (post[s] === POSE_AUTO) throw new FillStop(`${s} can't be "auto": ${pkg.type} has no pose set for it. Give a pose code like P04.`);
    throw new FillStop(`${s} must be a pose code like P04${pkg.pose_sets?.[s] ? ' or "auto"' : ''} (got "${post[s] ?? ''}")`);
  }
  const optional = new Set(pkg.optional_slots);
  const missing = pkg.slots.filter(s => s !== 'theme' && !optional.has(s) && !auto.has(s) && !String(post[s] ?? '').trim());
  if (missing.length) throw new FillStop(`these fields are empty: ${missing.join(', ')}`);
  const unknown = Object.keys(post).filter(k => !k.startsWith('_') && !['format', 'theme', 'caption'].includes(k) && !pkg.slots.includes(k));
  if (unknown.length) throw new FillStop(`these fields are not slots of ${pkg.template}: ${unknown.join(', ')}`);
}

/** The item words the Game word check looks at (item1.word, item2.word, ...). */
export function itemWords(post) {
  return Object.keys(post).filter(k => /^item\d+\.word$/.test(k)).map(k => String(post[k]).trim()).filter(Boolean);
}

// Removes an element whose only content is the empty optional slot, e.g. <p class="cover-sub" data-slot="cover.sub">{{cover.sub}}</p>.
export function removeEmptySlotElement(html, slot, file) {
  const s = slot.replace(/\./g, '\\.');
  const re = new RegExp(`<([a-z0-9]+)\\b[^>]*\\bdata-slot="${s}"[^>]*>\\{\\{${s}\\}\\}</\\1>`, 'g');
  const out = html.replace(re, '');
  if (out === html && html.includes(`{{${slot}}}`)) {
    throw new FillStop(`${file}: cannot remove the empty optional slot ${slot} the way the slots doc describes (its element holds more than the placeholder)`);
  }
  return out;
}

// Removes every data-optional element whose slots are all empty (slots doc step 3).
export function removeEmptyOptionalBlocks(html, values) {
  let from = 0;
  for (;;) {
    const at = html.indexOf('data-optional', from);
    if (at < 0) return html;
    const open = html.lastIndexOf('<', at);
    const tag = /^<([a-z0-9]+)/.exec(html.slice(open))[1];
    const tagRe = new RegExp(`<(/?)${tag}\\b[^>]*>`, 'g');
    tagRe.lastIndex = open;
    let depth = 0, end = -1, m;
    while ((m = tagRe.exec(html))) {
      depth += m[1] ? -1 : 1;
      if (depth === 0) { end = m.index + m[0].length; break; }
    }
    if (end < 0) throw new FillStop('a data-optional element in the template is not closed');
    const block = html.slice(open, end);
    const slots = [...block.matchAll(/\{\{([a-z0-9_.]+)\}\}/g)].map(x => x[1]);
    if (slots.length && slots.every(sl => !String(values[sl] ?? '').trim())) html = html.slice(0, open) + html.slice(end);
    else from = end;
  }
}

/**
 * Fills one template: escape every value, [x] -> <em class="k">x</em> on the package's accent slots,
 * empty optional slots and blocks removed. poses maps each pose slot to its image src (a data: URI).
 */
export function fillTemplate(html, file, post, poses, pkg) {
  if (/fonts\.(googleapis|gstatic)\.com/.test(html)) throw new FillStop(`${file} loads fonts from Google`);
  const theme = post.theme || /data-default-theme="([a-z-]+)"/.exec(html)?.[1];
  if (!theme) throw new FillStop(`${file} has no data-default-theme`);
  const values = { ...post, theme, ...poses };
  const accents = new Set(pkg.accent_slots);
  const poseSlots = new Set(pkg.pose_slots);
  html = removeEmptyOptionalBlocks(html, values);
  for (const slot of pkg.optional_slots) if (!String(values[slot] ?? '').trim()) html = removeEmptySlotElement(html, slot, file);
  return html.replace(/\{\{([a-z0-9_.]+)\}\}/g, (whole, slot) => {
    const v = values[slot];
    if (v === undefined || v === null || (!poseSlots.has(slot) && slot !== 'theme' && !String(v).trim())) {
      throw new FillStop(`${file}: no value for {{${slot}}}`);
    }
    let out = escapeHtml(v);
    if (accents.has(slot)) out = out.replace(/\[(.+?)\]/g, '<em class="k">$1</em>');
    return out;
  });
}

/**
 * Reads one slide's fit line, as the template's own fit script writes it ("63% full, word 140 px").
 * fill is null on cover slides (no percentage). bad: over its limit or does not fit.
 */
export function slideFit(text, limit) {
  const t = String(text ?? '');
  const pct = /(\d+)% full/.exec(t);
  const fill = pct ? Number(pct[1]) : null;
  const bad = /DOES NOT FIT|Over \d+%/.test(t) || (fill !== null && fill > limit);
  return { fill, bad };
}

/**
 * Judges a whole platform version from its fit lines: every slide measured, none over the limit.
 * Returns { ok, slides: [{ slide, text, fill, bad }], problems: [string] }.
 */
export function judgeFits(fits, limit, expectedSlides) {
  const problems = [];
  const slides = (Array.isArray(fits) ? fits : []).map((text, i) => ({ slide: i + 1, text: String(text ?? ''), ...slideFit(text, limit) }));
  if (expectedSlides && slides.length !== expectedSlides) problems.push(`expected ${expectedSlides} slides, measured ${slides.length}`);
  for (const s of slides) {
    if (!s.text.trim()) problems.push(`slide ${s.slide}: not measured`);
    else if (s.bad) problems.push(`slide ${s.slide}: "${s.text}"`);
  }
  return { ok: problems.length === 0, slides, problems };
}
